/**
 * Session Persistence Hook
 * Complete session persistence system for The Architect
 * Handles saving, loading, resuming, and managing user sessions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api.js';

export const useSessionPersistence = () => {
    const { user, isAuthenticated, authType } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
    const autoSaveTimeoutRef = useRef(null);

    // Auto-save configuration
    const AUTO_SAVE_DELAY = 10000; // 10 seconds
    const MAX_AUTO_SAVE_SESSIONS = 100; // Keep a large number of auto-saved sessions

    /**
     * Save current session state to backend
     */
    const saveSession = useCallback(async (sessionData, options = {}) => {
        if (!isAuthenticated || authType !== 'authenticated') {
            return null;
        }

        try {
            const {
                title = options.title || generateSessionTitle(sessionData),
                description = options.description || '',
                isAutoSave = false,
                overwriteExisting = false
            } = options;

            const payload = {
                sessionId: sessionData.sessionId,
                title,
                description,
                sessionData: {
                    originalIdea: sessionData.originalIdea,
                    platform: sessionData.platform,
                    research: sessionData.research,
                    validation: sessionData.validation,
                    answers: sessionData.answers || [],
                    analysis: sessionData.analysis,
                    prompts: sessionData.prompts,
                    flowDiagram: sessionData.flowDiagram,
                    currentStep: sessionData.currentStep || 1,
                    completedSteps: sessionData.completedSteps || []
                },
                metadata: {
                    isAutoSave,
                    completionPercentage: calculateCompletionPercentage(sessionData),
                    lastActiveStep: sessionData.currentStep || 1,
                    estimatedCreditsUsed: sessionData.estimatedCreditsUsed || 0,
                    platform: sessionData.platform,
                    createdAt: sessionData.createdAt || new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                }
            };


            const response = await fetch(`${API_CONFIG.apiURL}/session/save`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Failed to save session: ${response.status}`);
            }

            const result = await response.json();
            
            if (result && result.success && result.sessionId) {
                
                // Update local sessions list
                setSessions(prev => {
                    const existing = prev.find(s => s && s.sessionId === result.sessionId);
                    if (existing) {
                        return prev.map(s => s && s.sessionId === result.sessionId ? {
                            ...s,
                            ...result.session,
                            updatedAt: new Date().toISOString()
                        } : s);
                    } else {
                        return [result.session, ...prev.slice(0, 19)]; // Keep last 20
                    }
                });

                return result.session;
            } else {
                throw new Error(result.error || 'Unknown error saving session');
            }

        } catch (error) {
            if (import.meta.env.DEV) {
                // Development error logging removed for production
            }
            setError(`Failed to save session: ${error.message}`);
            return null;
        }
    }, [isAuthenticated, authType]);

    /**
     * Load user's saved sessions from backend
     */
    const loadUserSessions = useCallback(async (options = {}) => {
        if (!isAuthenticated || authType !== 'authenticated') {
            return [];
        }

        try {
            setLoading(true);
            setError(null);

            const {
                limit = 200,
                includeAutoSave = false,
                sortBy = 'updatedAt',
                sortOrder = 'desc'
            } = options;

            const params = new URLSearchParams({
                limit: limit.toString(),
                includeAutoSave: includeAutoSave.toString(),
                sortBy,
                sortOrder
            });

            const response = await fetch(`${API_CONFIG.apiURL}/session/history?${params}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load sessions: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setSessions(result.sessions || []);
                return result.sessions || [];
            } else {
                throw new Error(result.error || 'Unknown error loading sessions');
            }

        } catch (error) {
            setError(`Failed to load sessions: ${error.message}`);
            return [];
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, authType]);

    /**
     * Load a specific session by ID
     */
    const loadSession = useCallback(async (sessionId) => {
        if (!isAuthenticated || authType !== 'authenticated') {
            return null;
        }

        try {
            setLoading(true);
            setError(null);


            const response = await fetch(`${API_CONFIG.apiURL}/session?id=${sessionId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load session: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success && result.session) {
                
                // Calculate current step based on what data exists
                let currentStep = 1; // default to created
                if (result.session.research) currentStep = 2;
                if (result.session.validation) currentStep = 3; 
                if (result.session.analysis) currentStep = 4;
                if (result.session.prompts) currentStep = 5;
                if (result.session.flowDiagram) currentStep = 6;
                
                // Parse JSON strings to objects for frontend compatibility
                const parseJsonField = (field, fieldName) => {
                    if (!field) return field;
                    if (typeof field === 'string') {
                        try {
                            const parsed = JSON.parse(field);
                            return parsed;
                        } catch (error) {
                            return field;
                        }
                    }
                    return field;
                };

                const parsedAnalysis = parseJsonField(result.session.analysis, 'analysis');
                const parsedResearch = parseJsonField(result.session.research, 'research');
                const parsedValidation = parseJsonField(result.session.validation, 'validation');
                const parsedPrompts = parseJsonField(result.session.prompts, 'prompts');
                const parsedFlowDiagram = parseJsonField(result.session.flowDiagram, 'flowDiagram');


                return {
                    sessionId: result.session.id,
                    title: result.session.title || `${result.session.idea} (${result.session.platform})`,
                    description: result.session.description || '',
                    sessionData: {
                        sessionId: result.session.id,
                        originalIdea: result.session.idea,
                        platform: result.session.platform,
                        research: parsedResearch,
                        validation: parsedValidation,
                        answers: result.session.answers || [],
                        analysis: parsedAnalysis,
                        prompts: parsedPrompts,
                        flowDiagram: parsedFlowDiagram,
                        currentStep: currentStep,
                        userId: null // Will be set by the system
                    },
                    ...result.session
                };
            } else {
                throw new Error(result.error || 'Session not found');
            }

        } catch (error) {
            setError(`Failed to load session: ${error.message}`);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, authType]);

    /**
     * Delete a saved session
     */
    const deleteSession = useCallback(async (sessionId) => {
        if (!isAuthenticated || authType !== 'authenticated') {
            return false;
        }

        try {

            const response = await fetch(`${API_CONFIG.apiURL}/session/save`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sessionId })
            });

            if (!response.ok) {
                throw new Error(`Failed to delete session: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                // Remove from local sessions list
                setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
                return true;
            } else {
                throw new Error(result.error || 'Unknown error deleting session');
            }

        } catch (error) {
            setError(`Failed to delete session: ${error.message}`);
            return false;
        }
    }, [isAuthenticated, authType]);

    /**
     * Auto-save session with debouncing
     */
    const scheduleAutoSave = useCallback((sessionData) => {
        if (!autoSaveEnabled || !isAuthenticated || authType !== 'authenticated') {
            return;
        }

        // Clear existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Schedule new auto-save
        autoSaveTimeoutRef.current = setTimeout(async () => {
            try {
                await saveSession(sessionData, {
                    title: `Auto-save: ${generateSessionTitle(sessionData)}`,
                    isAutoSave: true
                });
                
                // Clean up old auto-save sessions
                await cleanupAutoSaveSessions();
                
            } catch (error) {
            }
        }, AUTO_SAVE_DELAY);

    }, [autoSaveEnabled, isAuthenticated, authType, saveSession]);

    /**
     * Clean up old auto-save sessions (keep only the latest ones)
     */
    const cleanupAutoSaveSessions = useCallback(async () => {
        const autoSaveSessions = sessions.filter(s => s.metadata?.isAutoSave);
        
        if (autoSaveSessions.length > MAX_AUTO_SAVE_SESSIONS) {
            const oldSessions = autoSaveSessions
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .slice(MAX_AUTO_SAVE_SESSIONS);

            for (const session of oldSessions) {
                await deleteSession(session.sessionId);
            }
        }
    }, [sessions, deleteSession]);

    /**
     * Generate a descriptive title for a session
     */
    const generateSessionTitle = useCallback((sessionData) => {
        if (!sessionData) return 'Untitled Session';
        
        const idea = sessionData.originalIdea || '';
        const platform = sessionData.platform || '';
        
        // Extract key words from the idea
        const ideaWords = idea.split(' ').slice(0, 5).join(' ');
        const truncatedIdea = ideaWords.length > 30 ? ideaWords.substring(0, 30) + '...' : ideaWords;
        
        return platform ? `${truncatedIdea} (${platform})` : truncatedIdea || 'Untitled Session';
    }, []);

    /**
     * Calculate completion percentage for a session
     */
    const calculateCompletionPercentage = useCallback((sessionData) => {
        if (!sessionData) return 0;

        // 🔧 ENHANCED: More robust completion calculation with proper data validation
        const steps = [
            // Step 1: Idea & Platform (required for workflow start)
            sessionData.originalIdea && sessionData.platform,

            // Step 2: Research (check for research object with meaningful content)
            sessionData.research && (
                typeof sessionData.research === 'object' ?
                Object.keys(sessionData.research).length > 0 :
                sessionData.research.length > 50
            ),

            // Step 3: Validation (check for validation object with meaningful content)
            sessionData.validation && (
                typeof sessionData.validation === 'object' ?
                Object.keys(sessionData.validation).length > 0 :
                sessionData.validation.length > 50
            ),

            // Step 4: Answers (require at least 1 answer)
            Array.isArray(sessionData.answers) && sessionData.answers.length > 0,

            // Step 5: Analysis (check for analysis object with meaningful content)
            sessionData.analysis && (
                typeof sessionData.analysis === 'object' ?
                Object.keys(sessionData.analysis).length > 0 :
                sessionData.analysis.length > 50
            ),

            // Step 6: Prompts (check for prompts object with meaningful content)
            sessionData.prompts && (
                typeof sessionData.prompts === 'object' ?
                Object.keys(sessionData.prompts).length > 0 :
                sessionData.prompts.length > 50
            ),

            // Step 7: Diagrams (check all possible field names with content validation)
            (sessionData.flowDiagram && Object.keys(sessionData.flowDiagram).length > 0) ||
            (sessionData.flow_diagram && Object.keys(sessionData.flow_diagram).length > 0) ||
            (sessionData.diagrams && Object.keys(sessionData.diagrams).length > 0) ||
            (sessionData.diagram && Object.keys(sessionData.diagram).length > 0)
        ];

        const completedSteps = steps.filter(Boolean).length;
        const percentage = Math.round((completedSteps / steps.length) * 100);

        return percentage;
    }, []);

    /**
     * Load sessions when user authenticates
     */
    useEffect(() => {
        if (isAuthenticated && authType === 'authenticated') {
            loadUserSessions();
        } else {
            setSessions([]);
            setError(null);
        }
    }, [isAuthenticated, authType, loadUserSessions]);

    /**
     * Cleanup timeouts
     */
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, []);

    return {
        // Session data
        sessions,
        loading,
        error,

        // Session operations
        saveSession,
        loadSession,
        loadUserSessions,
        deleteSession,

        // Auto-save
        scheduleAutoSave,
        autoSaveEnabled,
        setAutoSaveEnabled,

        // Utilities
        generateSessionTitle,
        calculateCompletionPercentage,

        // Status
        canSaveSessions: isAuthenticated && authType === 'authenticated'
    };
};

export default useSessionPersistence;